import { StrictMode } from 'react'
import { demoAtivo } from './dev/demo-ativo'
import { createRoot } from 'react-dom/client'
import { RouterProvider } from '@tanstack/react-router'
import { registerSW } from 'virtual:pwa-register'
import { createFirebaseAccountRepository } from '@adapters/firebase/account-repository'
import { getFirebase } from '@adapters/firebase/app'
import { createFirebaseAuthGateway } from '@adapters/firebase/auth-gateway'
import { createFirebaseCategoryRepository } from '@adapters/firebase/category-repository'
import { createFirebaseCreditCardRepository } from '@adapters/firebase/credit-card-repository'
import { createFirebaseHouseholdRepository } from '@adapters/firebase/household-repository'
import { createFirebaseBreakdownRepository } from '@adapters/firebase/breakdown-repository'
import { createFirebaseCommentRepository } from '@adapters/firebase/comment-repository'
import { createFirebaseRecadoRepository } from '@adapters/firebase/recado-repository'
import { createFirebaseNotificationRepository } from '@adapters/firebase/notification-repository'
import { createFirebaseProductRepository } from '@adapters/firebase/product-repository'
import { createFirebaseListRepository } from '@adapters/firebase/list-repository'
import { createFirebaseReceiptRepository } from '@adapters/firebase/receipt-repository'
import { createFirebaseAgentInstructionsRepository } from '@adapters/firebase/agent-instructions-repository'
import { createFirebaseCategoryRuleRepository } from '@adapters/firebase/category-rule-repository'
import { createOpenFoodFactsLookup } from '@adapters/openfoodfacts/lookup'
import { createFirebaseInvoiceRepository } from '@adapters/firebase/invoice-repository'
import { createFirebaseTransactionRepository } from '@adapters/firebase/transaction-repository'
import { createProviderAccountRepository } from '@adapters/firebase/provider-account-repository'
import { AppProviders } from '@ui/shell/AppProviders'
import { avisarNovaVersao } from '@ui/shell/nova-versao'
import { router } from '@ui/routes/router'
import './styles/app.css'

// **`onNeedReload` DESLIGA o recarregamento automático**, e essa é a razão de
// ele estar aqui. Sem ele, o registrador do `vite-plugin-pwa` em `autoUpdate`
// chama `window.location.reload()` no instante em que o service worker novo
// ativa — no meio do que a pessoa estiver fazendo. Ver `nova-versao.ts`.
registerSW({ immediate: true, onNeedReload: avisarNovaVersao })

const container = document.getElementById('root')
if (!container) throw new Error('root não encontrado')
// A raiz é criada UMA vez, fora da função assíncrona: `createRoot` duas vezes
// no mesmo nó é um aviso do React em dev e uma árvore órfã em produção.
const raiz = createRoot(container)

// **A composição é aqui, e só aqui.** `src/main.tsx` é o único arquivo do
// repositório que pode importar `adapters/` — a fronteira de camada
// (eslint.config.js, provada em src/test/eslint-boundaries.test.ts) proíbe
// `ui → adapters`, e é por isso que o `AuthProvider` recebe a porta pronta em
// vez de ir buscar o Firebase sozinho. O efeito prático é que a árvore inteira
// da UI roda em teste com uma porta dublada, sem mock de módulo do SDK.
//
// A árvore de providers em si mora no `AppProviders`, e o `renderApp` do teste
// usa **a mesma**: enquanto eram duas cópias, esquecer uma porta aqui não
// quebrava teste nenhum. Agora é erro de compilação — e são seis portas desde a
// Task 8 da Fase 2.
const { auth, db, functions, storage } = getFirebase()
const authGateway = createFirebaseAuthGateway({ auth, functions })
const householdRepository = createFirebaseHouseholdRepository(db)
const accountRepository = createFirebaseAccountRepository(db)
const creditCardRepository = createFirebaseCreditCardRepository(db)
const categoryRepository = createFirebaseCategoryRepository(db)
const transactionRepository = createFirebaseTransactionRepository(db)
const providerAccountRepository = createProviderAccountRepository(db)
const invoiceRepository = createFirebaseInvoiceRepository(db)
const breakdownRepository = createFirebaseBreakdownRepository(db)
const commentRepository = createFirebaseCommentRepository(db, auth)
const recadoRepository = createFirebaseRecadoRepository(db, auth)
const notificationRepository = createFirebaseNotificationRepository(db, auth)
const productRepository = createFirebaseProductRepository(db, storage, auth)
const listRepository = createFirebaseListRepository(db, auth)
// `crypto.randomUUID` só para o caminho da imagem ser único — ver o adapter. A
// origem da aleatoriedade fica no composition root, e não dentro do adapter,
// pelo mesmo motivo de sempre: assim o teste consegue congelá-la.
const receiptRepository = createFirebaseReceiptRepository(db, storage, () => crypto.randomUUID())
const agentInstructionsRepository = createFirebaseAgentInstructionsRepository(db)
const categoryRuleRepository = createFirebaseCategoryRuleRepository(db)
// Direto do navegador: a API é aberta e responde CORS, e uma callable fria
// custaria segundos numa conveniência de preencher formulário. Ver o adapter.
const productLookup = createOpenFoodFactsLookup()

/**
 * A composição de produção. O demo troca ESTE objeto e nada mais — nenhuma
 * tela sabe qual dos dois recebeu.
 */
const producao = {
  authGateway,
  householdRepository,
  accountRepository,
  creditCardRepository,
  categoryRepository,
  transactionRepository,
  providerAccountRepository,
  invoiceRepository,
  breakdownRepository,
  commentRepository,
  recadoRepository,
  notificationRepository,
  productRepository,
  listRepository,
  receiptRepository,
  agentInstructionsRepository,
  categoryRuleRepository,
  productLookup,
}

/**
 * Monta o app, com os dublês do demo quando ele está ligado.
 *
 * `import.meta.env.DEV` vira `false` literal no build de produção, e o Rollup
 * elimina o ramo inteiro — junto com o `import()` que ele guarda. É o que
 * garante que dado de mentira não viaje para o app que mostra dinheiro de
 * verdade; há um teste conferindo isso no `dist`.
 */
async function montar() {
  let composicao = producao
  if (import.meta.env.DEV && demoAtivo()) {
    const { montarDemo } = await import('./dev/demo')
    const demo = montarDemo()
    composicao = {
      ...producao,
      ...demo.finance,
      authGateway: demo.authGateway,
      householdRepository: demo.householdRepository,
    }
    console.info('Porquinho: modo demo. Saia com ?sem-demo na URL.')
  }

  raiz.render(
    <StrictMode>
      <AppProviders {...composicao}>
        <RouterProvider router={router} />
      </AppProviders>
    </StrictMode>,
  )
}

void montar()
